import React, { useState, useCallback, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { Box, Typography, Paper, Stack, IconButton, SxProps, Theme } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { AddAPhotoOutlined, Close, VideocamOutlined } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { isEmpty } from '../../utils/lodash';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import CustomButton from '../Button/CustomButton';
import { RectangularImageCropperModal } from '../ImageCropper/RectangularImageCropperModal';
import { RICH_MEDIA_SHARE_SIZE_HINT } from '../../constants/richMediaShareImage';
import CloseIcon from '../CustomSVGIcons/CloseIcon';

export type TImage = { type: 'image' | 'video'; src: string; rows: number; cols: number };

interface PhotoUploadModalProps {
    onChange: (items: TImage[], rawFiles?: File[]) => void;
    onCancelMediaUpload: () => void;
    initialValue?: TImage[];
    aspect?: number;
    /** Crop to 1200×630 and compress to ≤500KB (contest/campaign/audition covers). */
    richMediaShare?: boolean;
    supportedTypes?: ('image' | 'video')[];
    maxCount?: number;
    sx?: SxProps<Theme>;
    emptyStateSx?: SxProps<Theme>;
    emptyStateIconSx?: SxProps<Theme>;
    emptyStateTitleSx?: SxProps<Theme>;
    emptyStateSubtitleSx?: SxProps<Theme>;
}

const PhotoUploadModal: React.FC<PhotoUploadModalProps> = ({
    onChange,
    onCancelMediaUpload,
    initialValue,
    aspect,
    richMediaShare = false,
    supportedTypes = ['image'],
    maxCount = 1,
    sx,
    emptyStateSx,
    emptyStateIconSx,
    emptyStateTitleSx,
    emptyStateSubtitleSx,
}) => {
    const { themeStyle } = useThemeContext();
    const onChangeRef = React.useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    const [mediaItems, setMediaItems] = useState<TImage[]>([]);
    const [cropperOpen, setCropperOpen] = useState(false);
    const [src, setSrc] = useState<string>('');
    const [pendingFilesQueue, setPendingFilesQueue] = useState<File[]>([]);
    const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        if (initialValue) setMediaItems(initialValue);
    }, [initialValue]);

    // Check if we can add more items
    const canAddMore = mediaItems.length < maxCount;
    // Get the appropriate icon based on supported types
    const getUploadIcon = () => {
        if (supportedTypes.includes('image') && supportedTypes.includes('video')) {
            return <AddAPhotoOutlined sx={{ fontSize: 24, color: theme[themeStyle].tag_white }} />;
        } else if (supportedTypes.includes('video')) {
            return <VideocamOutlined sx={{ fontSize: 24, color: theme[themeStyle].tag_white }} />;
        } else {
            return <AddAPhotoOutlined sx={{ fontSize: 24, color: theme[themeStyle].tag_white }} />;
        }
    };

    // Get the appropriate text based on supported types and count
    const getUploadText = () => {
        const isMultiple = maxCount > 1;
        const hasBothTypes = supportedTypes.includes('image') && supportedTypes.includes('video');

        if (hasBothTypes) {
            return isMultiple ? 'Add Photos/Videos' : 'Add Photo/Video';
        } else if (supportedTypes.includes('video')) {
            return isMultiple ? 'Add Videos' : 'Add Video';
        } else {
            return isMultiple ? 'Add Photos' : 'Add Photo';
        }
    };

    // Get the "Add More" button text
    const getAddMoreText = () => {
        const hasBothTypes = supportedTypes.includes('image') && supportedTypes.includes('video');
        const multi = maxCount > 1;

        if (hasBothTypes) {
            return multi ? 'Add More Videos / Photos' : 'Add More Video / Photo';
        }
        if (supportedTypes.includes('video')) {
            return multi ? 'Add More Videos' : 'Add More Video';
        }
        return multi ? 'Add More Photos' : 'Add More Photo';
    };

    // Function to calculate optimal grid layout based on image count
    const getGridLayout = (imageCount: number) => {
        if (imageCount === 1) {
            return { cols: 1, rowHeight: 200, gap: 0 };
        } else if (imageCount === 2) {
            return { cols: 2, rowHeight: 200, gap: 2 };
        } else if (imageCount === 3) {
            return { cols: 3, rowHeight: 180, gap: 2 };
        } else if (imageCount === 4) {
            return { cols: 2, rowHeight: 180, gap: 2 };
        } else if (imageCount === 5) {
            return { cols: 3, rowHeight: 180, gap: 2 };
        } else if (imageCount === 6) {
            return { cols: 3, rowHeight: 180, gap: 2 };
        } else if (imageCount <= 9) {
            return { cols: 3, rowHeight: 180, gap: 2 };
        } else {
            return { cols: 4, rowHeight: 180, gap: 2 };
        }
    };

    // Function to calculate item layout based on position and total count
    const getItemLayout = (index: number, totalCount: number) => {
        // if (totalCount === 1) {
        //     return { cols: 1, rows: 1 };
        // } else if (totalCount === 2) {
        //     return { cols: 1, rows: 1 };
        // } else if (totalCount === 3) {
        //     return { cols: 1, rows: 1 };
        // } else if (totalCount === 4) {
        //     return { cols: 1, rows: 1 };
        // } else if (totalCount === 5) {
        //     return { cols: 1, rows: 1 };
        // } else if (totalCount === 6) {
        //     return { cols: 1, rows: 1 };
        // } else {
        //     return { cols: 1, rows: 1 };
        // }

        if (totalCount === 3 && index === 0) {
            return { cols: 2, rows: 2 };
        }
        return { cols: 1, rows: 1 };
    };

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (acceptedFiles.length > 0 && canAddMore) {
                setPendingFilesQueue(prev => [...prev, ...acceptedFiles]);
            }
        },
        [canAddMore],
    );

    // Build accept object based on supported types
    const getAcceptObject = () => {
        const accept: Record<string, string[]> = {};

        if (supportedTypes.includes('image')) {
            accept['image/png'] = [];
            accept['image/jpeg'] = [];
            accept['image/webp'] = [];
        }

        if (supportedTypes.includes('video')) {
            accept['video/*'] = [];
        }

        return accept;
    };

    const { getRootProps, getInputProps, open } = useDropzone({
        accept: getAcceptObject(),
        multiple: maxCount > 1,
        onDrop,
        disabled: !canAddMore,
    });

    const processNextFile = useCallback(() => {
        if (processing || cropperOpen || pendingFilesQueue.length === 0) return;
        const file = pendingFilesQueue[0];

        // react-easy-crop only supports images; videos must skip the cropper and keep a real video URL for <video>.
        if (supportedTypes.includes('video') && file.type.startsWith('video/')) {
            const objectUrl = URL.createObjectURL(file);
            const newItem: TImage = { type: 'video', src: objectUrl, rows: 1, cols: 1 };
            let nextItems: TImage[];
            let nextFiles: File[];
            flushSync(() => {
                setMediaItems(prev => {
                    nextItems = [...prev, newItem];
                    return nextItems;
                });
                setAcceptedFiles(prev => {
                    nextFiles = [...prev, file];
                    return nextFiles;
                });
            });
            const notify = onChangeRef.current;
            if (typeof notify === 'function') {
                notify(nextItems!, nextFiles!);
            }
            setPendingFilesQueue(prev => prev.slice(1));
            setProcessing(false);
            return;
        }

        setProcessing(true);
        setSrc(URL.createObjectURL(file));
        setCropperOpen(true);
    }, [pendingFilesQueue, cropperOpen, processing, supportedTypes]);

    const compressImage = (base64: string, quality = 0.7): Promise<string> => {
        return new Promise(resolve => {
            const img = new window.Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0);
                canvas.toBlob(
                    blob => {
                        if (blob) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                resolve(reader.result as string);
                            };
                            reader.readAsDataURL(blob);
                        } else {
                            resolve(base64);
                        }
                    },
                    'image/jpeg',
                    quality,
                );
            };
            img.src = base64;
        });
    };

    const handleCropSave = async (base64: string, originalFile: File) => {
        setCropperOpen(false);
        setSrc('');

        // Determine file type
        const fileType = originalFile.type.startsWith('video/') ? 'video' : 'image';

        let processedSrc = base64;
        if (fileType === 'image' && !richMediaShare) {
            processedSrc = await compressImage(base64, 0.7);
        }

        const newItem: TImage = { type: fileType, src: processedSrc, rows: 1, cols: 1 };

        let nextItems: TImage[];
        let nextFiles: File[];
        flushSync(() => {
            setMediaItems(prevItems => {
                nextItems = [...prevItems, newItem];
                return nextItems;
            });
            setAcceptedFiles(prev => {
                nextFiles = [...prev, originalFile];
                return nextFiles;
            });
        });
        const notify = onChangeRef.current;
        if (typeof notify === 'function') {
            notify(nextItems!, nextFiles!);
        }

        setPendingFilesQueue(prev => {
            const nextQueue = prev.slice(1);
            setTimeout(() => {
                setProcessing(false);
                processNextFile();
            }, 100);
            return nextQueue;
        });
    };

    const handleCropCancel = () => {
        setCropperOpen(false);
        setSrc('');

        setPendingFilesQueue(prev => {
            const nextQueue = prev.slice(1);
            setTimeout(() => {
                setProcessing(false);
                processNextFile();
            }, 100);
            return nextQueue;
        });
    };

    const handleRemoveImage = (idx: number) => {
        const removed = mediaItems[idx];
        if (removed?.type === 'video' && removed.src.startsWith('blob:')) {
            URL.revokeObjectURL(removed.src);
        }
        const updatedMediaItems = mediaItems.filter((_, i) => i !== idx);
        const updatedAcceptedFiles = acceptedFiles.filter((_, i) => i !== idx);

        setMediaItems(updatedMediaItems);
        setAcceptedFiles(updatedAcceptedFiles);

        const notify = onChangeRef.current;
        if (typeof notify === 'function') {
            notify(updatedMediaItems, updatedAcceptedFiles);
        }
    };

    useEffect(() => {
        if (!processing && !cropperOpen && pendingFilesQueue.length > 0) {
            processNextFile();
        }
    }, [pendingFilesQueue, processing, cropperOpen, processNextFile]);

    const panelBg = (sx as { backgroundColor?: string } | undefined)?.backgroundColor ?? theme[themeStyle].deep_grey;

    return (
        <Box
            sx={{
                width: '100%',
                flexShrink: 0,
                my: 2,
                border: `1px solid ${theme[themeStyle].tag_white}`,
                p: 0.5,
                borderRadius: 2,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                minHeight: isEmpty(mediaItems) ? undefined : 120,
                ...sx,
            }}>
            {isEmpty(mediaItems) ? (
                <Paper
                    elevation={0}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme[themeStyle].deep_grey,
                        borderRadius: '8px',
                        padding: '20px',
                        textAlign: 'center',
                        height: '200px',
                        cursor: canAddMore ? 'pointer' : 'default',
                        opacity: canAddMore ? 1 : 0.6,
                        ...emptyStateSx,
                    }}
                    {...getRootProps()}>
                    <input {...getInputProps()} />
                    <Box
                        sx={{
                            backgroundColor: theme[themeStyle].darkGray60,
                            borderRadius: '50%',
                            height: '40px',
                            width: '40px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            ...emptyStateIconSx,
                        }}>
                        {getUploadIcon()}
                    </Box>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontSize: '14px',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            ...emptyStateTitleSx,
                        }}>
                        {getUploadText()}
                    </Typography>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontSize: '10px',
                            marginBottom: '16px',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            ...emptyStateSubtitleSx,
                        }}>
                        {richMediaShare ? RICH_MEDIA_SHARE_SIZE_HINT : 'supports jpeg / png / webp'}
                    </Typography>
                    {maxCount > 1 && (
                        <Typography
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontSize: '10px',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}>
                            Max {maxCount} items
                        </Typography>
                    )}
                </Paper>
            ) : (
                <>
                    {/*
                     * Dropzone's file input must stay mounted whenever we call `open()`.
                     * The empty state mounts it inside the Paper; that input unmounts once
                     * there are thumbnails, so "Add more" would otherwise do nothing.
                     */}
                    <input {...getInputProps()} style={{ display: 'none' }} />
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                            top: 0,
                            width: '100%',
                            py: 0.75,
                            px: 0.5,
                            mb: 1,
                            backgroundColor: panelBg,
                            borderBottom: `1px solid ${theme[themeStyle].tag_white}22`,
                            flexShrink: 0,
                        }}>
                        <Box>
                            {canAddMore && (
                                <CustomButton
                                    label={getAddMoreText()}
                                    onClick={open}
                                    withIcon
                                    beginningIcon={
                                        supportedTypes.includes('image') && supportedTypes.includes('video') ? (
                                            <AddAPhotoOutlined
                                                sx={{
                                                    fontSize: 18,
                                                    color: theme[themeStyle].true_white,
                                                    mr: 1,
                                                }}
                                            />
                                        ) : supportedTypes.includes('video') ? (
                                            <VideocamOutlined
                                                sx={{
                                                    fontSize: 18,
                                                    color: theme[themeStyle].true_white,
                                                    mr: 1,
                                                }}
                                            />
                                        ) : (
                                            <AddAPhotoOutlined
                                                sx={{
                                                    fontSize: 18,
                                                    color: theme[themeStyle].true_white,
                                                    mr: 1,
                                                }}
                                            />
                                        )
                                    }
                                    customStyle={{
                                        borderRadius: '4px',
                                    }}
                                />
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CloseIcon
                                stroke={theme[themeStyle].tag_white}
                                onClick={() => {
                                    mediaItems.forEach(item => {
                                        if (item.type === 'video' && item.src.startsWith('blob:')) {
                                            URL.revokeObjectURL(item.src);
                                        }
                                    });
                                    setMediaItems([]);
                                    setAcceptedFiles([]);
                                    onCancelMediaUpload();
                                }}
                                style={{ cursor: 'pointer' }}
                            />
                        </Box>
                    </Stack>
                    <Box
                        sx={{
                            width: '100%',
                            flex: 1,
                            minHeight: 0,
                        }}>
                        <ImageList
                            variant="quilted"
                            cols={getGridLayout(mediaItems.length).cols}
                            gap={getGridLayout(mediaItems.length).gap}
                            rowHeight={getGridLayout(mediaItems.length).rowHeight}
                            sx={{
                                width: '100%',
                                margin: 0,
                                overflow: 'hidden',
                            }}>
                            {mediaItems.map((item, index) => {
                                const itemLayout = getItemLayout(index, mediaItems.length);
                                return (
                                    <ImageListItem
                                        key={index}
                                        cols={itemLayout.cols}
                                        rows={itemLayout.rows}
                                        sx={{
                                            overflow: 'hidden',
                                            borderRadius: 1,
                                            '& .MuiImageListItem-img': {
                                                objectFit: 'cover',
                                            },
                                        }}>
                                        {item.type === 'video' ? (
                                            <video
                                                src={item.src}
                                                controls
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                }}
                                            />
                                        ) : (
                                            <img
                                                src={item.src}
                                                alt={`media-${index}`}
                                                loading="lazy"
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                }}
                                                // onClick={() => window.open(item.src, '_blank')}
                                            />
                                        )}
                                        <IconButton
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: 4,
                                                right: 4,
                                                background: 'rgba(255,255,255,0.7)',
                                            }}
                                            onClick={e => {
                                                e.stopPropagation();
                                                handleRemoveImage(index);
                                            }}>
                                            <Close fontSize="small" />
                                        </IconButton>
                                    </ImageListItem>
                                );
                            })}
                        </ImageList>
                    </Box>
                </>
            )}
            <RectangularImageCropperModal
                open={cropperOpen}
                src={src}
                onSave={handleCropSave}
                onCancel={handleCropCancel}
                aspect={aspect || 1}
                variant={richMediaShare ? 'richMediaShare' : 'default'}
            />
        </Box>
    );
};

export default PhotoUploadModal;
